import React, { useState, useEffect } from 'react';
import { Download, Users, User, Calendar } from 'lucide-react';
import ExcelJS from 'exceljs';
import { API_URL } from '../config';
import { pauseNoteFor } from '../utils/pauseNote';

// ============ STYLES EXCEL ============
const COLORS = {
  headerBg: '2563EB',      // bleu foncé
  headerFont: 'FFFFFF',    // blanc
  totalBg: 'DBEAFE',       // bleu clair
  congesBg: 'FEF3C7',      // jaune clair
  totalGeneralBg: 'D1FAE5', // vert clair
  modulationPrevBg: 'F3E8FF', // violet clair
  modulationMoisBg: 'EDE9FE', // violet
  modulationTotalBg: 'DDD6FE', // violet foncé
  altRow1: 'F9FAFB',       // gris très clair
  altRow2: 'FFFFFF',       // blanc
  employeeColors: ['EFF6FF', 'FFF7ED', 'F0FDF4', 'FDF2F8', 'FFFBEB', 'F0F9FF', 'FEF2F2', 'ECFDF5']
};

const borderStyle = { style: 'thin', color: { argb: 'D1D5DB' } };
const borders = { top: borderStyle, bottom: borderStyle, left: borderStyle, right: borderStyle };

function applyHeaderStyle(row) {
  row.eachCell(cell => {
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.headerBg } };
    cell.font = { bold: true, color: { argb: COLORS.headerFont }, size: 11 };
    cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
    cell.border = borders;
  });
  row.height = 30;
}

function applyRowBg(row, argbColor) {
  row.eachCell(cell => {
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: argbColor } };
    cell.border = borders;
    cell.alignment = { vertical: 'middle' };
  });
}

function applySpecialRow(row, bgColor, bold = true) {
  row.eachCell(cell => {
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bgColor } };
    cell.font = { bold, size: 11 };
    cell.border = borders;
    cell.alignment = { vertical: 'middle' };
  });
}

function autoFitColumns(ws) {
  ws.columns.forEach(col => {
    let maxLen = 10;
    col.eachCell({ includeEmpty: true }, cell => {
      const val = cell.value ? cell.value.toString() : '';
      maxLen = Math.max(maxLen, val.length + 2);
    });
    col.width = Math.min(maxLen, 35);
  });
}

async function downloadWorkbook(wb, fileName) {
  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.click();
  URL.revokeObjectURL(url);
}

export default function ExportsPayroll({ pointages = [], employees = [], authUser }) {
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [selectedEmployeeId, setSelectedEmployeeId] = useState('');
  const [conges, setConges] = useState([]);
  const [modulationData, setModulationData] = useState([]);

  // Charger les congés acceptés
  useEffect(() => {
    const loadConges = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_URL}/conges?statut=accepte`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setConges(data);
        }
      } catch (err) {
        console.error('Erreur chargement congés:', err);
      }
    };
    loadConges();
  }, []);

  // Charger les données de modulation pour le mois sélectionné
  useEffect(() => {
    const loadModulation = async () => {
      try {
        const [year, month] = selectedMonth.split('-').map(Number);
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_URL}/modulation/recap/monthly/${year}/${month}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setModulationData(data);
        }
      } catch (err) {
        console.error('Erreur chargement modulation:', err);
      }
    };
    loadModulation();
  }, [selectedMonth]);

  // Calculer les heures de congés d'un employé pour un mois donné
  const getCongesHours = (employeeId, year, month) => {
    const employee = employees.find(e => e.id === employeeId);
    const hebdo = employee?.heures_hebdomadaires || 35;
    const contratMensuel = parseFloat((hebdo * 52 / 12).toFixed(2)); // base lissée (cohérent avec le backend)
    const lastDay = new Date(year, month, 0).getDate();
    const monthStartStr = `${year}-${String(month).padStart(2, '0')}-01`;
    const monthEndStr = `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
    // Jours ouvrables du mois entier (Lun-Sam) — dénominateur de la proratisation
    let joursOuvrablesMois = 0;
    {
      const c = new Date(monthStartStr + 'T12:00:00');
      const last = new Date(monthEndStr + 'T12:00:00');
      while (c <= last) { if (c.getDay() !== 0) joursOuvrablesMois++; c.setDate(c.getDate() + 1); }
    }
    const empId = parseInt(employeeId);
    // Seuls les congés PAYÉS créditent des heures (exclut arrêt maladie / congé sans solde)
    const PAID_LEAVE_TYPES = ['conges_payes', 'rtt'];
    const employeeConges = conges.filter(c => parseInt(c.employee_id) === empId && c.statut === 'accepte' && PAID_LEAVE_TYPES.includes(c.type));
    let totalJours = 0;
    employeeConges.forEach(conge => {
      const cDebut = conge.date_debut > monthStartStr ? conge.date_debut : monthStartStr;
      const cFin = conge.date_fin < monthEndStr ? conge.date_fin : monthEndStr;
      if (cDebut > cFin) return;
      const start = new Date(cDebut + 'T12:00:00');
      const end = new Date(cFin + 'T12:00:00');
      let current = new Date(start);
      while (current <= end) {
        if (current.getDay() !== 0) totalJours++;
        current.setDate(current.getDate() + 1);
      }
    });
    // Proratisation sur le contrat lissé (cohérent avec calculateMonthlyBalance côté backend)
    const heures = joursOuvrablesMois > 0 ? parseFloat((contratMensuel * totalJours / joursOuvrablesMois).toFixed(2)) : 0;
    return { jours: totalJours, heures };
  };

  const getModulationForEmployee = (employeeId) => {
    const mod = modulationData.find(m => m.employee_id === employeeId);
    if (!mod || mod.error) return { compteur_ancien: 0, solde_mensuel: 0, compteur_actuel: 0 };
    return {
      compteur_ancien: mod.compteur_ancien || 0,
      solde_mensuel: mod.solde_mensuel || 0,
      compteur_actuel: mod.compteur_actuel || 0
    };
  };

  const canExport = () => authUser && ['Patron', 'Super Manager'].includes(authUser.role_name);

  if (!canExport()) {
    return (
      <div className="bg-surface rounded-xl shadow-lg p-8 text-center">
        <p className="text-text-secondary">Accès réservé aux Patrons et Super Managers</p>
      </div>
    );
  }

  const getFilteredEmployees = () => {
    if (!authUser) return [];
    const employeesWithoutExtras = employees.filter(emp => emp.role_name !== 'Extra');
    if (['Patron', 'Super Manager'].includes(authUser.role_name)) return employeesWithoutExtras;
    if (authUser.role_name === 'Manager') return employeesWithoutExtras.filter(emp => emp.service_name === authUser.service_name);
    return [];
  };

  const filteredEmployees = getFilteredEmployees();

  const getMonthBounds = () => {
    const [year, month] = selectedMonth.split('-').map(Number);
    const lastDay = new Date(year, month, 0).getDate();
    return {
      year, month,
      monthStartStr: `${year}-${String(month).padStart(2, '0')}-01`,
      monthEndStr: `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`
    };
  };

  const filterMonthPointages = (monthStartStr, monthEndStr) => {
    return pointages.filter(p => {
      const employee = employees.find(e => e.id === p.user_id);
      if (employee && employee.role_name === 'Extra') return false;
      const dateMatch = p.date >= monthStartStr && p.date <= monthEndStr && p.statut === 'termine';
      if (authUser.role_name === 'Manager') return dateMatch && employee && employee.service_name === authUser.service_name;
      return dateMatch;
    });
  };

  // Créer une feuille détaillée pour un employé (réutilisé par les exports détaillés)
  const buildEmployeeSheet = (wb, employee, empPointages, year, month) => {
    const sheetName = `${employee.first_name} ${employee.last_name}`.substring(0, 31); // Excel limite à 31 caractères
    const ws = wb.addWorksheet(sheetName);

    // En-tête avec infos employé
    const titleRow = ws.addRow([`${employee.first_name} ${employee.last_name} — ${String(month).padStart(2, '0')}/${year}`]);
    titleRow.getCell(1).font = { bold: true, size: 14 };
    ws.mergeCells(ws.rowCount, 1, ws.rowCount, 7);
    const infoRow = ws.addRow([`${employee.role_name} | ${employee.service_name || 'Non assigné'} | Contrat ${employee.heures_hebdomadaires || 35}h/semaine`]);
    infoRow.getCell(1).font = { size: 11, color: { argb: '6B7280' } };
    ws.mergeCells(ws.rowCount, 1, ws.rowCount, 7);
    ws.addRow([]);

    const headers = ['Date', 'Arrivée', 'Départ', 'Pause Début', 'Pause Fin', 'Heures Travaillées', 'Note (pause)'];
    const headerRow = ws.addRow(headers);
    applyHeaderStyle(headerRow);

    // Lignes de pointages — la colonne Note indique précisément la pause prise en compte
    // (auto 45 min / 1 h formation / manuelle / aucune si événement ou < 4 h).
    empPointages.forEach((p, i) => {
      const row = ws.addRow([
        p.date, p.heure_arrivee || '', p.heure_depart || '',
        p.pause_debut || '', p.pause_fin || '',
        parseFloat(p.heures_travaillees) || 0, pauseNoteFor(p)
      ]);
      applyRowBg(row, i % 2 === 0 ? COLORS.altRow1 : COLORS.altRow2);
    });

    const totalHoursPointes = empPointages.reduce((sum, p) => sum + (parseFloat(p.heures_travaillees) || 0), 0);
    const congesData = getCongesHours(employee.id, year, month);
    const empModulation = getModulationForEmployee(employee.id);

    // Lignes récap
    const totalRow = ws.addRow(['TOTAL', '', '', '', '', parseFloat(totalHoursPointes.toFixed(2)), '']);
    applySpecialRow(totalRow, COLORS.totalBg);

    const congesRow = ws.addRow(['CONGÉS DU MOIS', '', '', '', `${congesData.jours} jours`, congesData.heures, 'Congés acceptés']);
    applySpecialRow(congesRow, COLORS.congesBg);

    const totalGenRow = ws.addRow(['TOTAL GÉNÉRAL', '', '', '', '', parseFloat((totalHoursPointes + congesData.heures).toFixed(2)), 'Pointages + Congés']);
    applySpecialRow(totalGenRow, COLORS.totalGeneralBg);

    ws.addRow([]);

    const modPrevRow = ws.addRow(['MODULATION MOIS PRÉCÉDENT', '', '', '', '', empModulation.compteur_ancien, 'Total cumulé avant ce mois']);
    applySpecialRow(modPrevRow, COLORS.modulationPrevBg);
    const modMoisRow = ws.addRow(['MODULATION CE MOIS', '', '', '', '', empModulation.solde_mensuel, 'Solde net du mois']);
    applySpecialRow(modMoisRow, COLORS.modulationMoisBg);
    const modTotalRow = ws.addRow(['MODULATION TOTALE', '', '', '', '', empModulation.compteur_actuel, 'Total cumulé actuel']);
    applySpecialRow(modTotalRow, COLORS.modulationTotalBg);
    modTotalRow.getCell(1).font = { bold: true, size: 12 };
    modTotalRow.getCell(6).font = { bold: true, size: 12 };

    autoFitColumns(ws);
    if (ws.getColumn(1).width < 28) ws.getColumn(1).width = 28;
  };

  // ============ EXPORT DÉTAILLÉ TOUS (une feuille par employé) ============
  const handleExportDetailedAll = async () => {
    const { year, month, monthStartStr, monthEndStr } = getMonthBounds();
    const monthPointages = filterMonthPointages(monthStartStr, monthEndStr);

    if (monthPointages.length === 0) { alert('Aucun pointage à exporter pour ce mois'); return; }

    // Grouper les pointages par employé
    const byEmployee = {};
    monthPointages.forEach(p => {
      if (!byEmployee[p.user_id]) byEmployee[p.user_id] = [];
      byEmployee[p.user_id].push(p);
    });

    const wb = new ExcelJS.Workbook();

    // Trier les employés par nom et créer une feuille par employé
    const employeeIds = Object.keys(byEmployee).map(Number);
    const sortedEmployees = employeeIds
      .map(id => employees.find(e => e.id === id))
      .filter(Boolean)
      .sort((a, b) => `${a.last_name} ${a.first_name}`.localeCompare(`${b.last_name} ${b.first_name}`));

    sortedEmployees.forEach(emp => {
      const empPointages = byEmployee[emp.id].sort((a, b) => a.date.localeCompare(b.date));
      buildEmployeeSheet(wb, emp, empPointages, year, month);
    });

    await downloadWorkbook(wb, `pointages_detailles_tous_${year}_${String(month).padStart(2, '0')}.xlsx`);
  };

  // ============ EXPORT RÉSUMÉ TOUS ============
  const handleExportSummaryAll = async () => {
    const { year, month, monthStartStr, monthEndStr } = getMonthBounds();
    const monthPointages = filterMonthPointages(monthStartStr, monthEndStr);

    if (monthPointages.length === 0) { alert('Aucun pointage à exporter pour ce mois'); return; }

    const employeeSummary = {};
    monthPointages.forEach(p => {
      if (!employeeSummary[p.user_id]) {
        const employee = employees.find(e => e.id === p.user_id);
        employeeSummary[p.user_id] = { employee, totalHours: 0, totalDays: new Set() };
      }
      employeeSummary[p.user_id].totalHours += parseFloat(p.heures_travaillees) || 0;
      employeeSummary[p.user_id].totalDays.add(p.date);
    });

    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet('Résumé Mensuel');

    const headers = ['Employé', 'Rôle', 'Service', 'Mois', 'Jours Travaillés', 'Total Heures Pointées',
      'Jours Congés', 'Heures Congés', 'Total Général (Pointé + Congés)', 'Moyenne/Jour',
      'Modulation Mois Précédent (h)', 'Modulation Ce Mois (h)', 'Modulation Totale (h)'];
    const headerRow = ws.addRow(headers);
    applyHeaderStyle(headerRow);

    const summaries = Object.values(employeeSummary)
      .sort((a, b) => `${a.employee?.last_name}`.localeCompare(`${b.employee?.last_name}`));

    summaries.forEach((summary, i) => {
      const cg = getCongesHours(summary.employee.id, year, month);
      const mod = getModulationForEmployee(summary.employee.id);
      const bgColor = i % 2 === 0 ? COLORS.altRow1 : COLORS.altRow2;

      const row = ws.addRow([
        `${summary.employee.first_name} ${summary.employee.last_name}`,
        summary.employee?.role_name || '', summary.employee?.service_name || '',
        `${String(month).padStart(2, '0')}/${year}`,
        summary.totalDays.size,
        parseFloat(summary.totalHours.toFixed(2)),
        cg.jours,
        cg.heures,
        parseFloat((summary.totalHours + cg.heures).toFixed(2)),
        summary.totalDays.size > 0 ? parseFloat((summary.totalHours / summary.totalDays.size).toFixed(2)) : 0,
        mod.compteur_ancien, mod.solde_mensuel, mod.compteur_actuel
      ]);
      applyRowBg(row, bgColor);
    });

    autoFitColumns(ws);
    await downloadWorkbook(wb, `resume_mensuel_tous_${year}_${String(month).padStart(2, '0')}.xlsx`);
  };

  // ============ EXPORT PAR SALARIÉ ============
  const handleExportDetailedEmployee = async () => {
    if (!selectedEmployeeId) { alert('Veuillez sélectionner un salarié'); return; }

    const { year, month, monthStartStr, monthEndStr } = getMonthBounds();
    const employee = employees.find(e => e.id === parseInt(selectedEmployeeId));
    if (!employee) { alert('Salarié non trouvé'); return; }

    const monthPointages = pointages.filter(p =>
      p.user_id === parseInt(selectedEmployeeId) &&
      p.date >= monthStartStr && p.date <= monthEndStr &&
      p.statut === 'termine'
    ).sort((a, b) => a.date.localeCompare(b.date));

    if (monthPointages.length === 0) { alert('Aucun pointage à exporter pour ce salarié ce mois-ci'); return; }

    const wb = new ExcelJS.Workbook();
    buildEmployeeSheet(wb, employee, monthPointages, year, month);

    await downloadWorkbook(wb, `pointages_${employee.first_name}_${employee.last_name}_${year}_${String(month).padStart(2, '0')}.xlsx`);
  };

  return (
    <div className="space-y-6">
      {/* En-tête avec sélection du mois */}
      <div className="bg-surface rounded-xl shadow-lg p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
          <div className="flex-1">
            <h2 className="text-2xl font-bold text-text-primary flex items-center gap-3">
              <Download className="w-7 h-7 text-blue-600 dark:text-blue-400" />
              Exports pour la Paie
            </h2>
            <p className="text-text-secondary mt-2">Exportez les pointages mensuels au format Excel</p>
          </div>
          <div className="flex flex-col gap-2 lg:min-w-[280px]">
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              <label className="text-sm font-semibold text-text-secondary">Mois à exporter</label>
            </div>
            <input
              type="month"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="w-full px-5 py-3 text-lg font-semibold border-2 border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-blue-50 dark:bg-blue-950/40 hover:bg-blue-100 dark:bg-blue-900/40 transition cursor-pointer"
            />
          </div>
        </div>
      </div>

      {/* Exports tous les employés */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl shadow-lg p-6 border-2 border-blue-200">
          <div className="flex items-center gap-3 mb-3">
            <Users className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            <h3 className="text-xl font-bold text-text-primary">Export Détaillé du Mois (Tous)</h3>
          </div>
          <p className="text-text-secondary mb-4">
            Exporte tous les pointages du mois pour tous les employés avec les détails complets
            (date, heures d'arrivée/départ, pauses, heures travaillées).
          </p>
          <button onClick={handleExportDetailedAll}
            className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition shadow-md">
            <Download className="w-5 h-5" />
            Export Détaillé du Mois (Tous)
          </button>
        </div>
        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl shadow-lg p-6 border-2 border-green-200">
          <div className="flex items-center gap-3 mb-3">
            <Users className="w-6 h-6 text-green-600 dark:text-green-400" />
            <h3 className="text-xl font-bold text-text-primary">Export Résumé du Mois (Tous)</h3>
          </div>
          <p className="text-text-secondary mb-4">
            Exporte un résumé mensuel pour tous les employés avec uniquement le total d'heures
            travaillées et le nombre de jours.
          </p>
          <button onClick={handleExportSummaryAll}
            className="flex items-center gap-2 px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold transition shadow-md">
            <Download className="w-5 h-5" />
            Export Résumé du Mois (Tous)
          </button>
        </div>
      </div>

      {/* Export par salarié */}
      <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl shadow-lg p-6 border-2 border-purple-200">
        <div className="flex items-center gap-3 mb-3">
          <User className="w-6 h-6 text-purple-600 dark:text-purple-400" />
          <h3 className="text-xl font-bold text-text-primary">Export par Salarié</h3>
        </div>
        <p className="text-text-secondary mb-4">
          Exporte tous les pointages du mois pour un salarié spécifique avec les détails complets.
        </p>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2">Sélectionner un salarié</label>
            <select value={selectedEmployeeId} onChange={(e) => setSelectedEmployeeId(e.target.value)}
              className="w-full px-4 py-2 border border-border-strong rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500">
              <option value="">-- Choisir un salarié --</option>
              {filteredEmployees.map(emp => (
                <option key={emp.id} value={emp.id}>{emp.first_name} {emp.last_name} - {emp.role_name}</option>
              ))}
            </select>
          </div>
          <button onClick={handleExportDetailedEmployee} disabled={!selectedEmployeeId}
            className="flex items-center gap-2 px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-semibold transition shadow-md disabled:opacity-50 disabled:cursor-not-allowed">
            <Download className="w-5 h-5" />
            Export Détaillé pour ce Salarié
          </button>
        </div>
      </div>
    </div>
  );
}
